<template>
  <div class="pdf-viewer">
    <div
      v-if="pdfProcess < 100"
      class="process-bar"
      :style="{ width: pdfProcess + '%' }"
    ></div>

    <div ref="scrollContainer" class="pdf-scroll-container" @scroll.passive="handleScroll">
      <div
        v-for="page in pages"
        :key="page.number"
        :ref="(element) => setPageElement(page.number, element)"
        class="pdf-page"
        :style="{ width: `${page.width}px`, height: `${page.height}px` }"
        :data-page="page.number"
      >
        <canvas :ref="(element) => setCanvas(page.number, element)" class="pdf-canvas"></canvas>
        <span v-if="page.rendering" class="page-loading">Đang tải trang {{ page.number }}...</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineExpose, defineProps, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist/legacy/build/pdf.mjs";

GlobalWorkerOptions.workerSrc = `${process.env.BASE_URL || "/"}js/pdf.worker.min.js`;

const MAX_CANVAS_PIXELS = 12 * 1024 * 1024;
const RANGE_CHUNK_SIZE = 4 * 1024 * 1024;
const VIEWPORT_MARGIN = "800px 0px";

const props = defineProps({
  source: {
    type: String,
    required: true
  },
  fileSize: {
    type: [Number, String],
    default: 0
  }
});

const emit = defineEmits(["error", "loadedData", "url-changed"]);
const scrollContainer = ref(null);
const pdfProcess = ref(0);
const pageCount = ref(0);
const currentPage = ref(1);
const scale = ref(0.6);
const pages = ref([]);

const canvasElements = new Map();
const pageElements = new Map();
const renderTasks = new Map();
const visiblePages = new Set();
let loadingTask = null;
let pdfDocument = null;
let pageObserver = null;
let documentGeneration = 0;
let scrollFrame = null;

const fileSizeInBytes = computed(() => {
  if (typeof props.fileSize === "number") return props.fileSize;
  const normalized = String(props.fileSize || "").trim().toLowerCase();
  const value = Number.parseFloat(normalized.replace(/,/g, ""));
  if (!Number.isFinite(value)) return 0;
  if (normalized.includes("gb")) return value * 1024 * 1024 * 1024;
  if (normalized.includes("mb")) return value * 1024 * 1024;
  if (normalized.includes("kb")) return value * 1024;
  return value;
});

function setCanvas(pageNumber, element) {
  if (element) canvasElements.set(pageNumber, element);
  else canvasElements.delete(pageNumber);
}

function setPageElement(pageNumber, element) {
  if (element) pageElements.set(pageNumber, element);
  else pageElements.delete(pageNumber);
}

function clearCanvas(pageNumber) {
  const canvas = canvasElements.get(pageNumber);
  if (!canvas) return;
  canvas.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
  canvas.width = 0;
  canvas.height = 0;
  canvas.style.width = "0px";
  canvas.style.height = "0px";
  const pageState = pages.value[pageNumber - 1];
  if (pageState) pageState.rendered = false;
}

async function cancelPageRender(pageNumber) {
  const active = renderTasks.get(pageNumber);
  if (active) {
    active.task.cancel();
    try {
      await active.task.promise;
    } catch (error) {
      if (error?.name !== "RenderingCancelledException") console.warn("PDF render cancellation failed:", error);
    }
    active.page.cleanup();
    renderTasks.delete(pageNumber);
  }
  const pageState = pages.value[pageNumber - 1];
  if (pageState) pageState.rendering = false;
  clearCanvas(pageNumber);
}

async function renderPage(pageNumber, generation = documentGeneration) {
  const pageState = pages.value[pageNumber - 1];
  if (!pdfDocument || !pageState || pageState.rendered || pageState.rendering || generation !== documentGeneration) return;

  pageState.rendering = true;
  let page;
  try {
    page = await pdfDocument.getPage(pageNumber);
    if (generation !== documentGeneration || !visiblePages.has(pageNumber)) {
      page.cleanup();
      return;
    }

    const viewport = page.getViewport({ scale: scale.value });
    pageState.width = Math.max(1, Math.floor(viewport.width));
    pageState.height = Math.max(1, Math.floor(viewport.height));
    await nextTick();

    const canvas = canvasElements.get(pageNumber);
    if (!canvas || generation !== documentGeneration || !visiblePages.has(pageNumber)) {
      page.cleanup();
      return;
    }

    const deviceScale = Math.min(window.devicePixelRatio || 1, 2);
    const safeScale = Math.min(
      deviceScale,
      Math.sqrt(MAX_CANVAS_PIXELS / Math.max(viewport.width * viewport.height, 1))
    );
    const outputScale = Math.max(safeScale, 0.1);
    const context = canvas.getContext("2d", { alpha: false });

    canvas.width = Math.max(1, Math.floor(viewport.width * outputScale));
    canvas.height = Math.max(1, Math.floor(viewport.height * outputScale));
    canvas.style.width = `${pageState.width}px`;
    canvas.style.height = `${pageState.height}px`;

    const task = page.render({
      canvasContext: context,
      viewport,
      transform: outputScale === 1 ? null : [outputScale, 0, 0, outputScale, 0, 0]
    });
    renderTasks.set(pageNumber, { task, page });
    await task.promise;

    if (generation === documentGeneration && visiblePages.has(pageNumber)) {
      pageState.rendered = true;
      emit("loadedData");
    }
  } catch (error) {
    if (error?.name !== "RenderingCancelledException" && generation === documentGeneration) emit("error", error);
  } finally {
    if (page) page.cleanup();
    renderTasks.delete(pageNumber);
    pageState.rendering = false;
  }
}

function setupPageObserver() {
  pageObserver?.disconnect();
  if (!scrollContainer.value) return;

  pageObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const pageNumber = Number(entry.target.dataset.page);
      if (entry.isIntersecting) {
        visiblePages.add(pageNumber);
        renderPage(pageNumber);
      } else {
        visiblePages.delete(pageNumber);
        cancelPageRender(pageNumber);
      }
    });
  }, {
    root: scrollContainer.value,
    rootMargin: VIEWPORT_MARGIN,
    threshold: 0
  });

  pageElements.forEach((element) => pageObserver.observe(element));
}

async function destroyDocument() {
  documentGeneration += 1;
  pageObserver?.disconnect();
  pageObserver = null;
  visiblePages.clear();
  cancelAnimationFrame(scrollFrame);
  scrollFrame = null;

  await Promise.all([...renderTasks.keys()].map(cancelPageRender));
  canvasElements.forEach((_, pageNumber) => clearCanvas(pageNumber));

  if (loadingTask) {
    await loadingTask.destroy().catch(() => {});
    loadingTask = null;
  }
  if (pdfDocument) {
    await pdfDocument.destroy().catch(() => {});
    pdfDocument = null;
  }

  pages.value = [];
  pageElements.clear();
  canvasElements.clear();
}

async function loadDocument() {
  await destroyDocument();
  const generation = documentGeneration;
  if (!props.source) return;

  pdfProcess.value = 0;
  pageCount.value = 0;
  currentPage.value = 1;
  scale.value = 0.6;

  try {
    loadingTask = getDocument({
      url: props.source,
      disableRange: false,
      disableStream: true,
      disableAutoFetch: true,
      rangeChunkSize: RANGE_CHUNK_SIZE,
      ...(fileSizeInBytes.value > 0 ? { length: fileSizeInBytes.value } : {})
    });
    loadingTask.onProgress = ({ loaded, total }) => {
      if (total) pdfProcess.value = Math.min((100 * loaded) / total, 100);
    };

    const document = await loadingTask.promise;
    if (generation !== documentGeneration) {
      await document.destroy();
      return;
    }

    loadingTask = null;
    pdfDocument = document;
    pageCount.value = document.numPages;

    const firstPage = await document.getPage(1);
    const firstViewport = firstPage.getViewport({ scale: scale.value });
    firstPage.cleanup();
    pages.value = Array.from({ length: document.numPages }, (_, index) => ({
      number: index + 1,
      width: Math.max(1, Math.floor(firstViewport.width)),
      height: Math.max(1, Math.floor(firstViewport.height)),
      rendering: false,
      rendered: false
    }));

    await nextTick();
    setupPageObserver();
  } catch (error) {
    if (generation === documentGeneration && error?.name !== "AbortException") emit("error", error);
  }
}

function handleScroll() {
  if (scrollFrame) return;
  scrollFrame = requestAnimationFrame(() => {
    scrollFrame = null;
    const container = scrollContainer.value;
    if (!container) return;
    const target = container.scrollTop + 12;
    let nearestPage = 1;
    for (const [pageNumber, element] of pageElements) {
      if (element.offsetTop <= target) nearestPage = pageNumber;
      else break;
    }
    currentPage.value = nearestPage;
  });
}

async function rerenderVisiblePages() {
  const activePages = [...visiblePages];
  await Promise.all(activePages.map(cancelPageRender));
  const ratio = scale.value / (pages.value[0]?.scale || 0.6);
  pages.value.forEach((page) => {
    page.width = Math.max(1, Math.floor(page.width * ratio));
    page.height = Math.max(1, Math.floor(page.height * ratio));
    page.scale = scale.value;
  });
  await nextTick();
  activePages.forEach((pageNumber) => renderPage(pageNumber));
}

watch(() => props.source, loadDocument, { immediate: true });
onBeforeUnmount(destroyDocument);

async function zoomIn() {
  if (scale.value >= 3) return;
  scale.value = Math.min(3, scale.value + 0.1);
  await rerenderVisiblePages();
}

async function zoomOut() {
  if (scale.value <= 0.4) return;
  scale.value = Math.max(0.4, scale.value - 0.1);
  await rerenderVisiblePages();
}

async function resetZoom() {
  scale.value = 0.6;
  await rerenderVisiblePages();
}

function scrollToPage(pageNumber) {
  pageElements.get(pageNumber)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function previousPage() {
  if (currentPage.value <= 1) return;
  currentPage.value -= 1;
  scrollToPage(currentPage.value);
}

function nextPage() {
  if (currentPage.value >= pageCount.value) return;
  currentPage.value += 1;
  scrollToPage(currentPage.value);
}

defineExpose({ zoomIn, zoomOut, resetZoom, scale, currentPage, pageCount, previousPage, nextPage });
</script>

<style scoped>
.pdf-viewer {
  width: 100%;
  height: 100%;
  min-height: 0;
}

.pdf-scroll-container {
  width: 100%;
  height: 100%;
  overflow: auto;
  padding: 16px 0;
}

.pdf-page {
  position: relative;
  margin: 0 auto 16px;
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
}

.pdf-canvas {
  display: block;
}

.page-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  color: #6b7280;
  transform: translate(-50%, -50%);
}

.process-bar {
  height: 4px;
  background-color: white;
  transition: width 0.3s ease;
}
</style>
